import { useQueryClient } from "@tanstack/react-query";
import api from "apis";
import {
  DocumentApproveRequest,
  DocumentDetailResponse,
} from "apis/data-contracts";
import { modalStore } from "components/Common/Alert/AlertManager";
import { DocumentForm } from "features/document/utils/getTempSaveRequest";
import getTempUpdateRequest from "features/document/utils/getTempUpdateRequest";
import { useRequestApprovalMutation } from "../mutations/useRequestApprovalMutation";
import { TempDocumentDetailResponseWithAny } from "../queries/useTempDocumentDetailQuery";
import useSaveTempDoc, { SaveTempDocProps } from "./useSaveTempDoc";
import useUpdateTempDoc from "./useUpdateTempDoc";

interface ApproveRequestProps {
  approverUuids?: string[];
  referenceUserUuids?: string[];
  successCallback?: () => void;
}

interface SaveAndApproveRequestProps {
  saveTempDocProps: Omit<SaveTempDocProps, "successCallback">;
  approvalRequestProps: ApproveRequestProps;
}

interface UpdateRequestProps<T> {
  tempDocumentDetail: TempDocumentDetailResponseWithAny;
  form: T;
  companyUuid?: string;
  tagUuids?: string[];
  freeTagUuids: string[];
  provinceName?: string;
  cityName?: string;
  lastLevel?: number;
}

interface UpdateTempDocProps<T> {
  updateRequestProps: UpdateRequestProps<T>;
  successCallback?: (documentUuid: string) => void;
}

interface UpdateAndApproveRequestProps<T> {
  updateRequestProps: UpdateRequestProps<T>;
  approvalRequestProps: ApproveRequestProps;
}

interface UpdateReferenceUserOptions {
  documentUuid: string;
  referenceUserUuids: string[];
  permissionType?: DocumentDetailResponse["permissionType"];
  currentStatus?: DocumentDetailResponse["approveStatus"] | string | null;
  successCallback?: () => void;
}

const REFERENCE_UPDATE_ERROR_MODAL_ID = "reference-user-update-error-modal";
const APPROVAL_ACTION_STATUSES = [
  "REVIEW_IN_PROGRESS",
  "REVIEW",
  "APPROVE_IN_PROGRESS",
  "APPROVE",
  "REQ_SUPPLEMENT",
] as const;

type ApprovalActionStatus = NonNullable<DocumentApproveRequest["action"]>;

const isApprovalActionStatus = (
  status?: string | null,
): status is ApprovalActionStatus =>
  !!status && APPROVAL_ACTION_STATUSES.includes(status as ApprovalActionStatus);

const toReferenceUserUuidList = (
  document?: Pick<DocumentDetailResponse, "referenceUsers"> | null,
) => document?.referenceUsers.map((user) => user.uuid).sort() ?? [];

const isSameReferenceUserList = (
  currentReferenceUserUuids: string[],
  nextReferenceUserUuids: string[],
) => {
  if (currentReferenceUserUuids.length !== nextReferenceUserUuids.length) {
    return false;
  }

  return currentReferenceUserUuids.every(
    (referenceUserUuid, index) =>
      referenceUserUuid === nextReferenceUserUuids[index],
  );
};

const useApproveRequest = () => {
  const queryClient = useQueryClient();
  const requestApprovalMutation = useRequestApprovalMutation();
  const { saveTempDoc } = useSaveTempDoc();
  const { updateTempDoc: updateTempDocWithTempSaveReq } = useUpdateTempDoc();

  const invalidateDocumentQueries = async (documentUuid: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: api.document.getDocumentDetailKey(documentUuid),
      }),
      queryClient.invalidateQueries({
        queryKey: api.document.getDocumentByAdminKey(documentUuid),
      }),
    ]);
  };

  function approveRequest(
    documentUuid: string,
    { approverUuids, referenceUserUuids, successCallback }: ApproveRequestProps,
  ) {
    requestApprovalMutation.mutate(
      {
        documentUuid,
        data: {
          approverUuids,
          referenceUserUuids,
        },
      },
      {
        onSuccess: () => {
          if (successCallback) successCallback();
        },
      },
    );
  }

  function saveAndApproveRequest({
    saveTempDocProps,
    approvalRequestProps,
  }: SaveAndApproveRequestProps) {
    saveTempDoc({
      ...saveTempDocProps,
      successCallback: (documentUuid) => {
        approveRequest(documentUuid, approvalRequestProps);
      },
    });
  }

  function updateTempDoc<T extends DocumentForm>({
    updateRequestProps,
    successCallback,
  }: UpdateTempDocProps<T>) {
    const documentUuid = updateRequestProps.tempDocumentDetail?.uuid;
    const updateRequest = getTempUpdateRequest(updateRequestProps);

    updateTempDocWithTempSaveReq({
      documentUuid,
      updateRequest,
      successCallback: () => {
        if (successCallback) successCallback(documentUuid);
      },
    });
  }

  function updateAndApproveRequest<T extends DocumentForm>({
    updateRequestProps,
    approvalRequestProps,
  }: UpdateAndApproveRequestProps<T>) {
    updateTempDoc({
      updateRequestProps,
      successCallback: (documentUuid) => {
        approveRequest(documentUuid, approvalRequestProps);
      },
    });
  }

  const isReferenceUserUpdateApplied = async (
    documentUuid: string,
    referenceUserUuids: string[],
  ) => {
    const sortedTargetReferenceUsers = [...referenceUserUuids].sort();
    const [detailResult, adminDetailResult] = await Promise.allSettled([
      api.document.getDocumentDetail(documentUuid),
      api.document.getDocumentByAdmin(documentUuid),
    ]);

    const detailReferenceUsers =
      detailResult.status === "fulfilled"
        ? toReferenceUserUuidList(detailResult.value.data.result)
        : null;

    if (
      detailReferenceUsers &&
      isSameReferenceUserList(detailReferenceUsers, sortedTargetReferenceUsers)
    ) {
      return true;
    }

    const adminReferenceUsers =
      adminDetailResult.status === "fulfilled"
        ? adminDetailResult.value.data.result.document.referenceUsers
            .map((user) => user.uuid)
            .sort()
        : null;

    return !!(
      adminReferenceUsers &&
      isSameReferenceUserList(adminReferenceUsers, sortedTargetReferenceUsers)
    );
  };

  const openReferenceUserUpdateErrorModal = () => {
    modalStore.unregister(REFERENCE_UPDATE_ERROR_MODAL_ID);
    modalStore.register({
      id: REFERENCE_UPDATE_ERROR_MODAL_ID,
      title: "일시적 오류 발생",
      content: "일시적인 오류가 발생했습니다. 잠시 후에 다시 시도해 주세요.",
    });
  };

  const requestReferenceUserUpdate = async ({
    documentUuid,
    referenceUserUuids,
    permissionType,
    currentStatus,
  }: Omit<UpdateReferenceUserOptions, "successCallback">) => {
    const commonRequestConfig = {
      headers: {
        "X-Skip-Error-Modal": "true",
      },
    };

    const shouldUseApproveRequest =
      permissionType === "OWNER" || currentStatus === "APPROVE";

    if (shouldUseApproveRequest) {
      return api.document.requestApproval(
        documentUuid,
        {
          referenceUserUuids,
        },
        commonRequestConfig,
      );
    }

    const request: DocumentApproveRequest = {
      referenceUserUuids,
    };

    if (isApprovalActionStatus(currentStatus)) {
      request.action = currentStatus;
    }

    return api.document.approvalAction(
      documentUuid,
      request,
      commonRequestConfig,
    );
  };

  async function updateReferenceUser({
    documentUuid,
    referenceUserUuids,
    permissionType,
    currentStatus,
    successCallback,
  }: UpdateReferenceUserOptions) {
    try {
      await requestReferenceUserUpdate({
        documentUuid,
        referenceUserUuids,
        permissionType,
        currentStatus,
      });

      await invalidateDocumentQueries(documentUuid);

      if (successCallback) successCallback();
    } catch (error) {
      const isApplied = await isReferenceUserUpdateApplied(
        documentUuid,
        referenceUserUuids,
      );

      if (isApplied) {
        await invalidateDocumentQueries(documentUuid);

        if (successCallback) successCallback();
        return;
      }

      openReferenceUserUpdateErrorModal();
      console.error("reference user update failed", error);
    }
  }

  return {
    approveRequest,
    saveAndApproveRequest,
    updateAndApproveRequest,
    updateTempDoc,
    updateReferenceUser,
  };
};

export default useApproveRequest;
